import React from 'react';
import copyIcon from '@image/copy-icon.svg';
import toast from 'react-hot-toast';
import { Button } from '@ax/ui';
import useAnalytics from '@/hooks/useAnalytics';
import { useAppSelector } from '@ax/redux-store';
import { ANALYTICS_CONSTANTS } from '@ax/constants';
import { isSingleSectionNote } from '@ax/redux-store';
import { selectNoteMarkdownContent } from '@ax/redux-store';

interface CopyToClipboardProps {
  contentRef: React.RefObject<HTMLDivElement> | null;
  section: string;
}

const CopyToClipboard = ({ contentRef, section }: CopyToClipboardProps) => {
  const isSingleSectionNoteShowing = useAppSelector(isSingleSectionNote);
  const noteMarkdownContent = useAppSelector(selectNoteMarkdownContent);
  const analytics = useAnalytics();
  const visitsState = useAppSelector((state) => state.selectedVisit);
  const noteId = visitsState.uuid;

  const copyContent = async () => {
    if (isSingleSectionNoteShowing) {
      try {
        await navigator.clipboard.writeText(noteMarkdownContent.content.replace(/[#_*~`]/g, ''));
        toast('Copied to clipboard', {
          duration: 1000,
          style: {
            borderRadius: '50px',
            background: '#e2e9ff',
            color: '#344bc6',
            boxShadow: 'none',
            fontSize: '14px',
            padding: '5px 10px',
          },
        });
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }

      return;
    }

    if (!contentRef || !contentRef.current) {
      console.error('Component reference is invalid.');
      return;
    }

    const range = document.createRange();
    range.selectNode(contentRef.current);

    const selection = window.getSelection();

    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);

      const copiedText = contentRef.current.innerText;
      const normalizedText = copiedText.replace(/\n{2,}/g, '\n');

      try {
        await navigator.clipboard.writeText(normalizedText);
        toast('Copied to clipboard', {
          duration: 1000,
          style: {
            borderRadius: '50px',
            background: '#e2e9ff',
            color: '#344bc6',
            boxShadow: 'none',
            fontSize: '14px',
            padding: '5px 10px',
          },
        });
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }

      if (selection) {
        selection.removeAllRanges();
      }
    } else {
      console.error('Clipboard API is not supported in this browser.');
    }

    analytics.onNoteCopied({
      noteSection: section,
      noteId: noteId,
      method: ANALYTICS_CONSTANTS.copyMethods.button,
      wordsCount: contentRef.current.innerText.replace(/\n/g, ' ').split(' ').filter(Boolean)
        .length,
    });
  };

  return (
    <>
      <Button
        label={section}
        className="flex items-center gap-2"
        image={copyIcon}
        small
        outline
        onClick={copyContent}
        disabled={!noteId}
      />
    </>
  );
};

export default CopyToClipboard;
